import React, { useState } from 'react';
import {
  ChevronDown,
  Info,
  MoreHorizontal,
  RefreshCw,
  Settings,
  SlidersHorizontal,
  X,
} from 'lucide-react';

// 学期学习成绩管理页面
// 页面结构：条件筛选卡片 + 结果卡片（名单表格）+ 右侧抽屉（维护学期学习成绩）
export default function AcademicScoreManagementPage() {
  // ================= 页面状态 =================
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // ================= 名单数据 =================
  const scoreRosterList = [
    {
      id: 1,
      name: '评奖评优基准名单',
      useStatus: '使用中',
      createdAt: '2026-05-09 14:18',
      referenceAt: '2026-05-09',
      academicYear: '2025-2026',
      semester: '第二学期',
    },
  ];

  return (
    <div className="animate-fade-in space-y-4">
      {/* 条件筛选卡片：按名单名称和学年学期查询 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
          <FilterInput
            label="名单名称"
            placeholder="请输入"
            widthClass="w-[280px]"
          />

          <FilterSelect
            label="学年/学期"
            widthClass="w-[280px]"
          />

          <div className="flex-1" />

          {/* 查询按钮区 */}
          <div className="flex gap-3">
            <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
              重置
            </button>
            <button className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]">
              查询
            </button>
          </div>
        </div>
      </div>

      {/* 结果卡片：学期学习成绩名单列表 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        {/* 表格工具按钮区 */}
        <div className="flex justify-end mb-6">
          <TableToolbar />
        </div>

        {/* 名单表格 */}
        <div className="overflow-x-auto border border-gray-200 rounded">
          <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap min-w-max">
            <thead>
              <tr className="bg-[#fafafa] border-b border-gray-200">
                <th className="py-3 px-4 w-10">
                  <input type="checkbox" className="rounded border-gray-300" />
                </th>
                <TableHead>序号</TableHead>
                <TableHead>名单名称</TableHead>
                <TableHead>使用状态</TableHead>
                <TableHead>创建时间</TableHead>
                <TableHead>基准参考时间点</TableHead>
                <TableHead>学年</TableHead>
                <TableHead>学期</TableHead>
                <th className="py-3 px-4 font-medium text-gray-800 sticky right-0 bg-[#fafafa] z-10 border-l border-gray-200">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {scoreRosterList.map((row, index) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 hover:bg-[#f8f9fa] group"
                >
                  <td className="py-3 px-4">
                    <input type="checkbox" className="rounded border-gray-300" />
                  </td>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.useStatus}</TableCell>
                  <TableCell>{row.createdAt}</TableCell>
                  <TableCell>{row.referenceAt}</TableCell>
                  <TableCell>{row.academicYear}</TableCell>
                  <TableCell>{row.semester}</TableCell>
                  <td className="py-3 px-4 sticky right-0 bg-white group-hover:bg-[#f8f9fa] z-10 border-l border-gray-100">
                    <button
                      onClick={() => setIsDrawerOpen(true)}
                      className="text-[#1890ff] hover:underline"
                    >
                      维护学期学习成绩
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 分页区 */}
        <SimplePagination total={scoreRosterList.length} />
      </div>

      {/* 右侧抽屉：维护学期学习成绩 */}
      {isDrawerOpen && (
        <ScoreMaintainDrawer onClose={() => setIsDrawerOpen(false)} />
      )}
    </div>
  );
}

// 维护学期学习成绩抽屉
function ScoreMaintainDrawer({ onClose }) {
  // ================= 抽屉状态 =================
  const [activeTab, setActiveTab] = useState('course');
  const [isStudentDetailOpen, setIsStudentDetailOpen] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30">
      {/* 抽屉主体：支持课程成绩和学生学期成绩两个维护视图 */}
      <div
        className="h-full bg-[#f3f6fa] shadow-[-8px_0_24px_rgba(0,0,0,0.15)] flex flex-col"
        style={{ width: 'min(1120px, calc((100vw - 240px) * 0.72))' }}
      >
        {/* 抽屉标题栏 */}
        <div className="h-[58px] px-6 bg-white border-b border-gray-200 flex items-center gap-3">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
          <h2 className="text-[16px] font-bold text-gray-900">
            维护学期学习成绩
          </h2>
        </div>

        {/* 抽屉 Tab：切换课程明细和学生学期成绩汇总 */}
        <div className="px-6 bg-white border-b border-gray-200 flex gap-8">
          <button
            onClick={() => setActiveTab('course')}
            className={`py-3 text-sm ${activeTab === 'course' ? 'text-[#1890ff] border-b-2 border-[#1890ff] font-bold' : 'text-gray-600 hover:text-[#1890ff]'}`}
          >
            学期课程成绩
          </button>
          <button
            onClick={() => setActiveTab('semester')}
            className={`py-3 text-sm ${activeTab === 'semester' ? 'text-[#1890ff] border-b-2 border-[#1890ff] font-bold' : 'text-gray-600 hover:text-[#1890ff]'}`}
          >
            学期学习成绩
          </button>
        </div>

        {/* 抽屉内容区 */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {activeTab === 'course' && (
            <CourseScoreTab />
          )}

          {activeTab === 'semester' && (
            <SemesterScoreTab onOpenStudentDetail={() => setIsStudentDetailOpen(true)} />
          )}
        </div>
      </div>

      {/* 二级右侧页面：查看单个学生导入的所有课程成绩 */}
      {isStudentDetailOpen && (
        <StudentCourseDetailDrawer onClose={() => setIsStudentDetailOpen(false)} />
      )}
    </div>
  );
}

// 学期课程成绩 Tab
function CourseScoreTab() {
  // ================= 课程成绩表格数据 =================
  const courseScoreList = [
    {
      id: 1,
      name: '倪**',
      studentId: '240104020001',
      academicYear: '2025-2026',
      semester: '第二学期',
      college: '经济学院',
      major: '金融学',
      grade: '2024',
      courseName: '金融学',
      courseCode: 'A0001',
      credit: '2.00',
      examScore: '95.00',
      countGpa: '是',
      countFail: '否',
    },
  ];

  return (
    <>
      {/* 条件筛选卡片：按学生、课程和成绩统计口径查询 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-8 gap-y-5">
          <FilterSelect
            label="学院/专业/班级"
            widthClass="w-full"
          />
          <FilterSelect
            label="是否计算学分绩点"
            widthClass="w-full"
          />
          <FilterSelect
            label="是否计算挂科门数"
            widthClass="w-full"
          />
          <FilterSelect
            label="年级"
            widthClass="w-full"
          />
          <FilterInput
            label="学号"
            placeholder="请输入学号"
            widthClass="w-full"
          />
          <FilterInput
            label="姓名"
            placeholder="请输入姓名"
            widthClass="w-full"
          />
          <FilterInput
            label="课程名称"
            placeholder="请输入课程名称"
            widthClass="w-full"
          />
          <FilterInput
            label="课程代码"
            placeholder="请输入课程代码"
            widthClass="w-full"
          />
        </div>

        {/* 查询按钮区 */}
        <div className="flex justify-end items-center gap-3 mt-6">
          <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
            重置
          </button>
          <button className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]">
            查询
          </button>
          <button className="text-[#1890ff] text-sm flex items-center gap-1">
            展开
            <ChevronDown size={15} />
          </button>
        </div>
      </div>

      {/* 结果卡片：课程成绩列表 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        {/* 表格上方操作按钮区 */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2">
            <button className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]">
              新增
            </button>
            <button className="px-5 py-2 border border-red-400 text-red-500 rounded text-sm hover:bg-red-50">
              删除
            </button>
            <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
              导入
            </button>
            <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
              学期学习成绩导出 按查询
            </button>
            <button className="px-3 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
              <MoreHorizontal size={16} />
            </button>
          </div>

          <TableToolbar />
        </div>

        {/* 课程成绩表格 */}
        <div className="overflow-x-auto border border-gray-200 rounded">
          <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap min-w-max">
            <thead>
              <tr className="bg-[#fafafa] border-b border-gray-200">
                <th className="py-3 px-4 w-10">
                  <input type="checkbox" className="rounded border-gray-300" />
                </th>
                <TableHead>序号</TableHead>
                <TableHead>姓名</TableHead>
                <TableHead>学号</TableHead>
                <TableHead>测评学年</TableHead>
                <TableHead>测评学期</TableHead>
                <TableHead>院系</TableHead>
                <TableHead>专业</TableHead>
                <TableHead>年级</TableHead>
                <TableHead>课程名称</TableHead>
                <TableHead>课程代码</TableHead>
                <TableHead>学分</TableHead>
                <TableHead>考试成绩</TableHead>
                <TableHead>是否计算学分绩点</TableHead>
                <TableHead>是否计算挂科门数</TableHead>
                <th className="py-3 px-4 font-medium text-gray-800 sticky right-0 bg-[#fafafa] z-10 border-l border-gray-200">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {courseScoreList.map((row, index) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 hover:bg-[#f8f9fa] group"
                >
                  <td className="py-3 px-4">
                    <input type="checkbox" className="rounded border-gray-300" />
                  </td>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.studentId}</TableCell>
                  <TableCell>{row.academicYear}</TableCell>
                  <TableCell>{row.semester}</TableCell>
                  <TableCell>{row.college}</TableCell>
                  <TableCell>{row.major}</TableCell>
                  <TableCell>{row.grade}</TableCell>
                  <TableCell>{row.courseName}</TableCell>
                  <TableCell>{row.courseCode}</TableCell>
                  <TableCell>{row.credit}</TableCell>
                  <TableCell>{row.examScore}</TableCell>
                  <TableCell>{row.countGpa}</TableCell>
                  <TableCell>{row.countFail}</TableCell>
                  <td className="py-3 px-4 sticky right-0 bg-white group-hover:bg-[#f8f9fa] z-10 border-l border-gray-100">
                    <button className="text-[#1890ff] hover:underline">
                      修改
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 分页区 */}
        <SimplePagination total={courseScoreList.length} />
      </div>
    </>
  );
}

// 学期学习成绩 Tab
function SemesterScoreTab({ onOpenStudentDetail }) {
  // ================= 学生学期成绩汇总数据 =================
  const semesterScoreList = [
    {
      id: 1,
      name: '倪**',
      studentId: '240104020001',
      academicYear: '2025-2026',
      semester: '第二学期',
      className: '金融学1班',
      averageGpa: '4.20',
      creditCourseCount: 6,
      college: '经济学院',
      grade: '2024',
      major: '金融学',
    },
  ];

  return (
    <>
      {/* 条件筛选卡片：按学年学期、班级、计算状态和学生信息查询 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-8 gap-y-5">
          <FilterSelect
            label="学年"
            widthClass="w-full"
          />
          <FilterSelect
            label="学期"
            widthClass="w-full"
          />
          <FilterSelect
            label="学院/专业/班级"
            widthClass="w-full"
          />
          <FilterSelect
            label="年级"
            widthClass="w-full"
          />
          <FilterSelect
            label="是否已计算"
            widthClass="w-full"
          />
          <FilterInput
            label="学号"
            placeholder="请输入学号"
            widthClass="w-full"
          />
          <FilterInput
            label="姓名"
            placeholder="请输入姓名"
            widthClass="w-full"
          />
        </div>

        {/* 查询按钮区 */}
        <div className="flex justify-end items-center gap-3 mt-6">
          <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
            重置
          </button>
          <button className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]">
            查询
          </button>
        </div>
      </div>

      {/* 结果卡片：学生学期学习成绩汇总 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        {/* 表格上方提示信息 */}
        <div className="mb-4 px-3 py-3 bg-[#eaf4ff] border border-[#d3e8ff] rounded text-sm text-[#1677ff] leading-6 flex gap-2">
          <Info size={16} className="mt-0.5 shrink-0" />
          <div>
            学年：2025-2026；学期：第二学期；学生人数：35人；已计算平均学分绩点人数：28人；尚未计算平均学分绩点人数：7人；最后一次计算时间：2026-05-09 14:18。
          </div>
        </div>

        {/* 表格上方操作按钮区 */}
        <div className="flex justify-between items-center mb-4">
          <button className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]">
            计算平均学分绩点
          </button>

          <TableToolbar />
        </div>

        {/* 学期学习成绩表格 */}
        <div className="overflow-x-auto border border-gray-200 rounded">
          <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap min-w-max">
            <thead>
              <tr className="bg-[#fafafa] border-b border-gray-200">
                <th className="py-3 px-4 w-10">
                  <input type="checkbox" className="rounded border-gray-300" />
                </th>
                <TableHead>序号</TableHead>
                <TableHead>姓名</TableHead>
                <TableHead>学号</TableHead>
                <TableHead>学年</TableHead>
                <TableHead>学期</TableHead>
                <TableHead>班级</TableHead>
                <TableHead>平均学分绩点</TableHead>
                <TableHead>计入学分课程数量</TableHead>
                <TableHead>学院</TableHead>
                <TableHead>年级</TableHead>
                <TableHead>专业</TableHead>
                <TableHead>班级</TableHead>
                <th className="py-3 px-4 font-medium text-gray-800 sticky right-0 bg-[#fafafa] z-10 border-l border-gray-200">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {semesterScoreList.map((row, index) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 hover:bg-[#f8f9fa] group"
                >
                  <td className="py-3 px-4">
                    <input type="checkbox" className="rounded border-gray-300" />
                  </td>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.studentId}</TableCell>
                  <TableCell>{row.academicYear}</TableCell>
                  <TableCell>{row.semester}</TableCell>
                  <TableCell>{row.className}</TableCell>
                  <TableCell>{row.averageGpa}</TableCell>
                  <TableCell>{row.creditCourseCount}</TableCell>
                  <TableCell>{row.college}</TableCell>
                  <TableCell>{row.grade}</TableCell>
                  <TableCell>{row.major}</TableCell>
                  <TableCell>{row.className}</TableCell>
                  <td className="py-3 px-4 sticky right-0 bg-white group-hover:bg-[#f8f9fa] z-10 border-l border-gray-100">
                    <button
                      onClick={onOpenStudentDetail}
                      className="text-[#1890ff] hover:underline"
                    >
                      详情
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 分页区 */}
        <SimplePagination total={semesterScoreList.length} />
      </div>
    </>
  );
}

// 学生导入课程成绩详情抽屉
function StudentCourseDetailDrawer({ onClose }) {
  const studentCourseList = [
    {
      id: 1,
      courseName: '金融学',
      courseCode: 'A0001',
      credit: '2.00',
      examScore: '95.00',
      countGpa: '是',
      countFail: '否',
    },
    {
      id: 2,
      courseName: '宏观经济学',
      courseCode: 'A0002',
      credit: '3.00',
      examScore: '88.00',
      countGpa: '是',
      countFail: '否',
    },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/20">
      <div
        className="h-full bg-[#f3f6fa] shadow-[-8px_0_24px_rgba(0,0,0,0.18)] flex flex-col"
        style={{ width: 'min(820px, calc((100vw - 240px) * 0.52))' }}
      >
        {/* 抽屉标题栏 */}
        <div className="h-[58px] px-6 bg-white border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-[16px] font-bold text-gray-900">
            学生导入课程成绩详情 - 倪**（240104020001）
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* 结果卡片：该学生本学期导入的所有课程成绩 */}
          <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
            <div className="overflow-x-auto border border-gray-200 rounded">
              <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap">
                <thead>
                  <tr className="bg-[#fafafa] border-b border-gray-200">
                    <TableHead>序号</TableHead>
                    <TableHead>课程名称</TableHead>
                    <TableHead>课程代码</TableHead>
                    <TableHead>学分</TableHead>
                    <TableHead>考试成绩</TableHead>
                    <TableHead>是否计算学分绩点</TableHead>
                    <TableHead>是否计算挂科门数</TableHead>
                  </tr>
                </thead>
                <tbody>
                  {studentCourseList.map((row, index) => (
                    <tr
                      key={row.id}
                      className="border-b border-gray-100 hover:bg-[#f8f9fa]"
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{row.courseName}</TableCell>
                      <TableCell>{row.courseCode}</TableCell>
                      <TableCell>{row.credit}</TableCell>
                      <TableCell>{row.examScore}</TableCell>
                      <TableCell>{row.countGpa}</TableCell>
                      <TableCell>{row.countFail}</TableCell>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 条件筛选：输入框
function FilterInput({ label, placeholder = '', widthClass = 'w-[280px]' }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-800 shrink-0 whitespace-nowrap">
        {label}：
      </span>
      <input
        placeholder={placeholder}
        className={`border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-[#1890ff] ${widthClass}`}
      />
    </div>
  );
}

// 条件筛选：下拉框
function FilterSelect({ label, placeholder = '请选择', widthClass = 'w-[280px]' }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-800 shrink-0 whitespace-nowrap">
        {label}：
      </span>
      <select className={`border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-[#1890ff] bg-white text-gray-500 ${widthClass}`}>
        <option>{placeholder}</option>
      </select>
    </div>
  );
}

// 表格工具按钮
function TableToolbar() {
  return (
    <div className="flex items-center gap-4 text-gray-700">
      <button className="hover:text-[#1890ff]" title="刷新">
        <RefreshCw size={18} />
      </button>
      <button className="hover:text-[#1890ff]" title="密度">
        <SlidersHorizontal size={18} />
      </button>
      <button className="hover:text-[#1890ff]" title="设置">
        <Settings size={18} />
      </button>
    </div>
  );
}

// 表格表头
function TableHead({ children }) {
  return (
    <th className="py-3 px-4 font-medium text-gray-800">
      {children}
    </th>
  );
}

// 表格单元格
function TableCell({ children }) {
  return (
    <td className="py-3 px-4">
      {children}
    </td>
  );
}

// 简单分页：用于展示当前页、总条数、每页条数
function SimplePagination({ total }) {
  return (
    <div className="flex justify-end items-center mt-4 text-sm text-gray-700 gap-2">
      <span>
        第1-1条 / 共{total}条
      </span>
      <button className="text-gray-300 px-1">
        &lt;
      </button>
      <button className="px-2.5 py-1 border border-[#1890ff] text-[#1890ff] rounded">
        1
      </button>
      <button className="text-gray-300 px-1">
        &gt;
      </button>
      <select className="border border-gray-300 rounded px-2 py-1 outline-none bg-white">
        <option>10 条/页</option>
      </select>
    </div>
  );
}
